import React, { memo } from "react";
import { Loader2, Sparkles, Download, Trash2, Phone, Mail, Check, X, Flag, Combine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import SocialOutreachButtons from "@/components/SocialOutreachButtons";
import PendingSocialsBadge from "@/components/PendingSocialsBadge";
import EnrichmentBar from "@/components/EnrichmentBar";
import { progressFromRealtor, progressFromSlim } from "@/lib/enrichment";
import PhotoAvatar from "./PhotoAvatar";
import { SourceBadge, ScanCountBadge, TopPerformerBadge } from "./RealtorBadges";

// Single desktop `<tr>` for the realtors table. Extracted + memoized so
// that a re-render of the parent (which happens every 3s while
// enrichment is polling, on every checkbox toggle, and on every search
// keystroke) does NOT force React to rebuild all 900 row DOM nodes.
//
// Only the props that actually change per-row cause a re-render:
//   • `realtor` — mutated in place via patchRealtor() after enrichment
//   • `selected` — checkbox flip on this specific row
//   • `progress` — live enrichment percentage for this specific realtor
//   • `enrichBusy` — while this realtor's enrich job is in-flight
//
// All handlers (`onToggle`, `onEnrich`, `onDownload`, `onDelete`,
// `onOpen`, `onPatch`) MUST be `useCallback`-wrapped by the parent or
// this memoization is defeated. See Realtors.jsx.
function RealtorRow({
  realtor,
  selected,
  progress,
  enrichBusy,
  onToggle,
  onEnrich,
  onDownload,
  onDelete,
  onOpen,
  onPatch,
  onContactLogged,
  onSocialVerified,
  onFollowChanged,
  onApprove,
  onReject,
  onMerge,
  onBrokerageClick,
  platformOrder,
}) {
  const r = realtor;
  const isPending = r.review_status === "pending";
  return (
    <tr className="border-t border-slate-200" data-testid={`realtor-row-${r.id}`}>
      <td className="px-3 py-2 align-middle">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggle(r.id)}
          data-testid={`realtor-select-${r.id}`}
        />
      </td>
      <td className="px-3 py-2 overflow-hidden">
        <button
          onClick={() => onOpen(r)}
          className="flex items-center gap-3 text-left group w-full min-w-0"
          data-testid={`realtor-name-${r.id}`}
        >
          <PhotoAvatar realtor={r} size="sm" />
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 truncate group-hover:text-[hsl(var(--brand-strong))] flex items-center gap-1.5">
                {r.flagged_at ? (
                  <Flag
                    className="h-3.5 w-3.5 text-rose-600 fill-rose-600 shrink-0"
                    aria-label="Flagged for follow-up"
                    data-testid={`flag-badge-${r.id}`}
                  />
                ) : null}
                <span className="truncate">
                  {r.name || `${r.first_name} ${r.last_name}`}
                </span>
              </div>
              {r.title ? (
                <div className="text-xs text-slate-500 truncate">{r.title}</div>
              ) : null}
            </div>
            <TopPerformerBadge realtor={r} compact />
            <SourceBadge source={r.source} compact />
          </div>
        </button>
      </td>
      <td className="px-3 py-2 hidden lg:table-cell overflow-hidden">
        {r.brokerage_id ? (
          <button
            type="button"
            onClick={() => onBrokerageClick?.(r.brokerage_id)}
            className="text-slate-700 truncate text-left hover:underline hover:text-slate-900 transition-colors"
            data-testid={`realtor-brokerage-${r.id}`}
          >
            {r.brokerage_name || "—"}
          </button>
        ) : (
          <div className="text-slate-700 truncate">{r.brokerage_name}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-0.5 text-xs">
          {r.phone ? (
            <span className="flex items-center gap-1 text-slate-700">
              <Phone className="h-3 w-3 text-slate-400" />
              {r.phone}
            </span>
          ) : null}
          {r.work_phone ? (
            <span
              className="flex items-center gap-1 text-slate-500 text-[11px]"
              title="Brokerage / office line from Realtor.ca"
              data-testid={`realtor-work-phone-${r.id}`}
            >
              <Phone className="h-3 w-3 text-slate-300" />
              {r.work_phone}
            </span>
          ) : null}
          {r.email ? (
            <a
              href={`mailto:${r.email}`}
              className="flex items-center gap-1 text-slate-600 hover:text-[hsl(var(--brand-strong))] truncate"
            >
              <Mail className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="truncate">{r.email}</span>
            </a>
          ) : null}
          {!r.phone && !r.email ? (
            <span className="text-slate-400 text-xs">No contact yet</span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2">
        <EnrichmentBar realtorId={r.id} progress={progress} />
      </td>
      <td className="px-3 py-2 text-center hidden md:table-cell align-middle">
        <ScanCountBadge realtor={r} />
      </td>
      <td className="px-3 py-2 hidden xl:table-cell">
        <div className="flex flex-col gap-1">
          <SocialOutreachButtons realtor={r} onContactLogged={onContactLogged} onSocialVerified={onSocialVerified} onFollowChanged={onFollowChanged} platformOrder={platformOrder} compact />
          <PendingSocialsBadge realtor={r} onChange={onPatch} onFollowChanged={onFollowChanged} compact />
        </div>
      </td>
      <td className="px-2 py-2 text-right">
        <div className="flex justify-end gap-0.5">
          {isPending && onApprove ? (
            <>
              {onMerge ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onMerge(r)}
                  title="Merge into an existing realtor"
                  className="h-7 w-7 p-0 text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                  data-testid={`merge-realtor-${r.id}`}
                >
                  <Combine className="h-3.5 w-3.5" />
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onApprove(r.id)}
                title="Approve — add to main list"
                className="h-7 w-7 p-0 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                data-testid={`approve-realtor-${r.id}`}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onReject(r.id)}
                title="Reject — soft-delete"
                className="h-7 w-7 p-0 text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                data-testid={`reject-realtor-${r.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEnrich(r)}
            disabled={enrichBusy}
            title="Enrich socials"
            className="h-7 w-7 p-0"
            data-testid={`enrich-realtor-${r.id}`}
          >
            {enrichBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDownload(r)}
            title="Download vCard"
            className="h-7 w-7 p-0"
            data-testid={`download-vcard-${r.id}`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(r)}
            title="Delete"
            className="h-7 w-7 p-0"
            data-testid={`delete-realtor-${r.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// Custom equality: only re-render this row when its own realtor doc /
// selection / progress / busy flag changed. Reference-stable handler
// callbacks are assumed (useCallback in the parent). Deep-equal realtor
// is intentionally NOT done — patchRealtor always produces a new
// reference for the target row so the shallow ref check is correct.
export default memo(RealtorRow, (prev, next) => {
  return (
    prev.realtor === next.realtor &&
    prev.selected === next.selected &&
    prev.progress === next.progress &&
    prev.enrichBusy === next.enrichBusy &&
    prev.onToggle === next.onToggle &&
    prev.onEnrich === next.onEnrich &&
    prev.onDownload === next.onDownload &&
    prev.onDelete === next.onDelete &&
    prev.onOpen === next.onOpen &&
    prev.onPatch === next.onPatch &&
    prev.onContactLogged === next.onContactLogged &&
    prev.onSocialVerified === next.onSocialVerified &&
    prev.onFollowChanged === next.onFollowChanged &&
    prev.onApprove === next.onApprove &&
    prev.onReject === next.onReject &&
    prev.onMerge === next.onMerge &&
    prev.platformOrder === next.platformOrder
  );
});

// Compute progress for a row, preferring live-poll data if available.
// Exported for the parent so the parent can memoise the map cheaply and
// hand a stable reference to each memoised row.
export function rowProgress(realtor, liveEntry) {
  return liveEntry ? progressFromSlim(liveEntry) : progressFromRealtor(realtor);
}
